import { useEffect, useRef } from "react";

/* Live circuit-board backdrop (login page).
   - Canvas 2D only (no WebGL): PCB-style orthogonal traces in faint slate,
     glowing pulses in var(--brand-accent) traveling at varied speeds,
     node pads that glow when a pulse passes, occasional soft flicker.
   - 60fps-friendly: capped trace/pulse counts, single clear+redraw pass.
   - Pauses when the tab is hidden; honors prefers-reduced-motion by
     rendering a static faint circuit texture instead.
   - White-label safe: accent read live from CSS vars (refreshed ~1s). */

const MAX_PULSES = 14;
const TRACE_ALPHA = 0.13;

const hexToRgb = (hex) => {
  const h = hex.replace("#", "");
  if (h.length !== 6) return [212, 160, 23];
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
};

export default function CircuitBackground({ className = "" }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    let raf = 0;
    let last = 0;
    let frame = 0;
    let traces = [];
    let nodes = [];
    let pulses = [];
    let accentRgb = [212, 160, 23];
    let resizeTimer = 0;
    let destroyed = false;

    const readAccent = () => {
      const v = getComputedStyle(document.documentElement)
        .getPropertyValue("--brand-accent").trim();
      if (v.startsWith("#")) accentRgb = hexToRgb(v);
    };

    const buildNetwork = () => {
      const w = canvas.clientWidth || 1;
      const h = canvas.clientHeight || 1;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      traces = [];
      nodes = [];
      pulses = [];
      const grid = 48;
      const count = Math.min(26, Math.max(10, Math.floor((w * h) / 46000)));
      for (let i = 0; i < count; i += 1) {
        let x = Math.round((Math.random() * w) / grid) * grid;
        let y = Math.round((Math.random() * h) / grid) * grid;
        const pts = [[x, y]];
        let dir = Math.floor(Math.random() * 4);
        const segs = 4 + Math.floor(Math.random() * 5);
        for (let s = 0; s < segs; s += 1) {
          const len = grid * (1 + Math.floor(Math.random() * 4));
          if (dir === 0) x += len; else if (dir === 1) y += len;
          else if (dir === 2) x -= len; else y -= len;
          x = Math.max(0, Math.min(w, x));
          y = Math.max(0, Math.min(h, y));
          pts.push([x, y]);
          dir = (dir + (Math.random() < 0.5 ? 1 : 3)) % 4;
        }
        let total = 0;
        const cum = [0];
        for (let p = 1; p < pts.length; p += 1) {
          total += Math.hypot(pts[p][0] - pts[p - 1][0], pts[p][1] - pts[p - 1][1]);
          cum.push(total);
        }
        if (total < grid) continue;
        traces.push({ pts, cum, total });
        nodes.push({ x: pts[0][0], y: pts[0][1], glow: 0 });
        nodes.push({ x: pts[pts.length - 1][0], y: pts[pts.length - 1][1], glow: 0 });
        // mid-joint pads
        for (let p = 1; p < pts.length - 1; p += 2) {
          nodes.push({ x: pts[p][0], y: pts[p][1], glow: 0 });
        }
      }
    };

    const spawnPulse = () => {
      if (!traces.length || pulses.length >= MAX_PULSES) return;
      const trace = traces[Math.floor(Math.random() * traces.length)];
      pulses.push({
        trace,
        dist: 0,
        speed: 40 + Math.random() * 110, // px/s — varied speeds
        size: 1.4 + Math.random() * 1.2,
      });
    };

    const pointAt = (trace, dist) => {
      const { pts, cum } = trace;
      for (let p = 1; p < cum.length; p += 1) {
        if (dist <= cum[p]) {
          const t = (dist - cum[p - 1]) / Math.max(1, cum[p] - cum[p - 1]);
          return [
            pts[p - 1][0] + (pts[p][0] - pts[p - 1][0]) * t,
            pts[p - 1][1] + (pts[p][1] - pts[p - 1][1]) * t,
          ];
        }
      }
      return pts[pts.length - 1];
    };

    const drawBase = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      ctx.clearRect(0, 0, w, h);
      ctx.lineWidth = 1;
      ctx.lineJoin = "round";
      ctx.strokeStyle = `rgba(148,163,184,${reduced ? 0.16 : TRACE_ALPHA})`;
      traces.forEach((t) => {
        ctx.beginPath();
        ctx.moveTo(t.pts[0][0], t.pts[0][1]);
        for (let p = 1; p < t.pts.length; p += 1) ctx.lineTo(t.pts[p][0], t.pts[p][1]);
        ctx.stroke();
      });
      const [r, g, b] = accentRgb;
      nodes.forEach((n) => {
        ctx.beginPath();
        ctx.arc(n.x, n.y, 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(148,163,184,${0.18 + Math.min(0.4, n.glow * 0.4)})`;
        ctx.fill();
        if (n.glow > 0.04) {
          ctx.beginPath();
          ctx.arc(n.x, n.y, 3.6, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(${r},${g},${b},${(n.glow * 0.55).toFixed(3)})`;
          ctx.stroke();
        }
      });
    };

    const tick = (ts) => {
      if (destroyed) return;
      const dt = Math.min(0.05, (ts - last) / 1000 || 0.016);
      last = ts;
      frame += 1;
      if (frame % 60 === 0) readAccent(); // follow live palette switches

      // decay node glow + occasional soft flicker
      nodes.forEach((n) => { n.glow *= 0.93; });
      if (Math.random() < 0.02 && nodes.length) {
        nodes[Math.floor(Math.random() * nodes.length)].glow = 0.5;
      }

      // advance pulses
      const [r, g, b] = accentRgb;
      pulses = pulses.filter((p) => (p.dist += p.speed * dt) < p.trace.total);
      if (Math.random() < 0.08) spawnPulse();

      drawBase();
      ctx.save();
      pulses.forEach((p) => {
        const [x, y] = pointAt(p.trace, p.dist);
        // wake the nearest node the pulse passes
        nodes.forEach((n) => {
          if (Math.abs(n.x - x) < 6 && Math.abs(n.y - y) < 6) n.glow = 1;
        });
        // faint tail
        const [tx, ty] = pointAt(p.trace, Math.max(0, p.dist - 14));
        ctx.strokeStyle = `rgba(${r},${g},${b},0.22)`;
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.moveTo(tx, ty);
        ctx.lineTo(x, y);
        ctx.stroke();
        // glowing head
        ctx.shadowBlur = 9;
        ctx.shadowColor = `rgba(${r},${g},${b},0.9)`;
        ctx.fillStyle = `rgba(${r},${g},${b},0.92)`;
        ctx.beginPath();
        ctx.arc(x, y, p.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      });
      ctx.restore();
      raf = requestAnimationFrame(tick);
    };

    const start = () => {
      cancelAnimationFrame(raf);
      last = 0;
      raf = requestAnimationFrame(tick);
    };

    const onVisibility = () => {
      if (reduced) return;
      if (document.hidden) cancelAnimationFrame(raf);
      else start();
    };

    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        buildNetwork();
        if (reduced) drawBase();
      }, 200);
    };

    readAccent();
    buildNetwork();
    if (reduced) {
      drawBase(); // static faint circuit texture only
    } else {
      for (let i = 0; i < 6; i += 1) spawnPulse();
      start();
      document.addEventListener("visibilitychange", onVisibility);
    }
    window.addEventListener("resize", onResize);

    return () => {
      destroyed = true;
      cancelAnimationFrame(raf);
      clearTimeout(resizeTimer);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <canvas ref={ref} aria-hidden="true" data-testid="circuit-background"
      className={`absolute inset-0 h-full w-full ${className}`} />
  );
}
